// JOBE-0044.4 / JOBE-0101: top-level route component for `/library`.
//
// After JOBE-0101, this page is a thin shell: it mounts the three
// providers (EntityTypesProvider / DocumentKindsProvider /
// CollectionsProvider), threads the chrome (saved-views rail, sort
// header, omni palette) into <EntityBrowser> as slot content / children,
// and lets the host own URL state + fetch + selection.
//
// The chrome components (LibrarySortHeader, LibrarySavedViewsRail,
// LibraryOmniPaletteBridge) read the host's state via
// useEntityBrowserContext() rather than re-instantiating the URL-state
// hook — this avoids a URL write-race that would surface as flapping
// chip state on saved-view clicks.
//
// Behaviour at `/library` is byte-identical to pre-extraction. The
// library's existing tests (states, chipVocabulary, EntityTypesProvider,
// useLibraryPreferences, …) pass unmodified.

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { EntityBrowser } from './entitybrowser/EntityBrowser.jsx';
import { useEntityBrowserContext } from './entitybrowser/EntityBrowserContext.js';
import { LibraryRail } from './library/LibraryRail.jsx';
import { EntityTypesProvider } from './library/EntityTypesProvider.jsx';
import { EntityTypesContext } from './library/EntityTypesContext.js';
import { DocumentKindsProvider } from './library/DocumentKindsProvider.jsx';
import { DocumentKindsContext } from './library/DocumentKindsContext.js';
import { CollectionsProvider } from './library/CollectionsProvider.jsx';
import { CollectionsContext } from './library/CollectionsContext.js';
import { useLibraryPreferences } from './library/useLibraryPreferences.js';
import {
  FRIENDLY_TOKENS, chipsToFetchQuery,
} from './library/chipVocabulary.js';
import { useStore } from './lib/store.js';
import { useDocumentTitle } from './lib/useDocumentTitle.js';
import { OmniPalette } from './components/OmniPalette/OmniPalette.jsx';
import './library/library.css';

export function LibraryListPage() {
  // JOBE-0193: per-route document title.
  useDocumentTitle('Library');
  return (
    <EntityTypesProvider>
      <DocumentKindsProvider>
        <CollectionsProvider>
          <LibraryListPageInner />
        </CollectionsProvider>
      </DocumentKindsProvider>
    </EntityTypesProvider>
  );
}

function LibraryListPageInner() {
  const { typeRail, setTypeRail, density, setDensity } = useLibraryPreferences();

  // JOBE-0044.10.1: roster for the chip composer's PeopleValuePopover.
  // Humans have no BFF endpoint yet — empty array, gracefully handled.
  const agentsList = useStore((s) => s.agents.list);
  const roster = useMemo(
    () => ({ humans: [], agents: agentsList || [] }),
    [agentsList],
  );

  // JOBE-0044.9: shared create-modal flag. The rail's "+ Save view"
  // button toggles its local state; the omni palette's ⌘↩ chord
  // toggles this top-level flag so the same modal opens either way.
  const [paletteCreateOpen, setPaletteCreateOpen] = useState(false);
  const onPaletteOpenCreate = useCallback(() => setPaletteCreateOpen(true), []);
  const onCloseCreate = useCallback(() => setPaletteCreateOpen(false), []);

  // The library's rail (saved views + type rail + collections) reads
  // active chip / view state from the host context; passing typeRail
  // prefs through is the only thing the outer page still owns directly.
  const rail = (
    <LibrarySavedViewsRail
      typeRail={typeRail}
      setTypeRail={setTypeRail}
      createOpen={paletteCreateOpen}
      onCreateModalClose={onCloseCreate}
    />
  );
  const header = <LibrarySortHeader density={density} setDensity={setDensity} />;

  return (
    <div className="library-page">
      <EntityBrowser roster={roster} rail={rail} header={header} anchor={{ structuralShape: 'document' }}>
        <LibraryOmniPaletteBridge onSaveView={onPaletteOpenCreate} />
      </EntityBrowser>
    </div>
  );
}

// LibrarySortHeader: the library's page title + sort dropdown + density
// toggle (JOBE-0044.7). Reads sort state from the host context; density
// is owned by the page (via useLibraryPreferences) and threaded in.
function LibrarySortHeader({ density, setDensity }) {
  const { sort, setSort } = useEntityBrowserContext();
  return (
    <header className="library-page-header">
      <h1 className="library-page-title">Library</h1>
      <div className="library-page-sort">
        <label htmlFor="library-sort-select" className="muted">sort</label>
        <select
          id="library-sort-select"
          className="library-sort-select"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
        >
          <option value="lastChangedAt:desc">updated ↓</option>
          <option value="lastChangedAt:asc">updated ↑</option>
          <option value="name:asc">name A→Z</option>
          <option value="name:desc">name Z→A</option>
        </select>
      </div>
      <div
        className="library-page-density"
        role="group"
        aria-label="Row density"
      >
        <button
          type="button"
          className={
            'library-page-density-btn' +
            (density === 'comfortable' ? ' library-page-density-btn-active' : '')
          }
          onClick={() => setDensity && setDensity('comfortable')}
          aria-pressed={density === 'comfortable'}
        >
          comfy
        </button>
        <button
          type="button"
          className={
            'library-page-density-btn' +
            (density === 'compact' ? ' library-page-density-btn-active' : '')
          }
          onClick={() => setDensity && setDensity('compact')}
          aria-pressed={density === 'compact'}
        >
          compact
        </button>
      </div>
    </header>
  );
}

// LibrarySavedViewsRail: the library's outer rail. Reads chip/view
// state from the host context and dispatches via the host's setters.
// JOBE-0044.9: also threads the live chip set + an after-save hook
// down to SavedViewsBlock so the modal can capture the current chips
// at save time and re-anchor the URL to the freshly-minted view.
function LibrarySavedViewsRail({ typeRail, setTypeRail, createOpen, onCreateModalClose }) {
  const { chips, view, setChips, setView } = useEntityBrowserContext();

  // Derive the currently-active `type:` chip value (if exactly one) so
  // the type rail can highlight the matching row.
  const activeType = (() => {
    const c = chips.find(
      (chip) => chip.kind === 'predicate' && chip.friendlyToken === 'type',
    );
    if (!c || !c.values || c.values.length !== 1) return null;
    return c.values[0];
  })();

  // Saved-view click: write `?view=<id>` and replace the chip set with
  // the resolved chips. The view anchor is exclusive of chip params —
  // any further facet edit drops `view=` via onPickType / onPickCollection.
  const onPickView = useCallback((viewId, resolvedChips) => {
    setView(viewId);
    if (Array.isArray(resolvedChips)) {
      setChips(resolvedChips);
    }
  }, [setView, setChips]);

  // Type rail click: single-select per R3.
  const onPickType = useCallback((kind) => {
    setView(null);
    setChips((prev) => {
      const withoutType = prev.filter(
        (c) => !(c.kind === 'predicate' && c.friendlyToken === 'type'),
      );
      if (!kind) return withoutType;
      return [...withoutType, {
        kind: 'predicate',
        friendlyToken: 'type',
        wirePredicate: FRIENDLY_TOKENS.type.wirePredicate,
        values: [kind],
      }];
    });
  }, [setChips, setView]);

  // Collection rail click: append an `in:<slug>` chip and dissolve the
  // view anchor.
  const onPickCollection = useCallback((collection) => {
    if (!collection) return;
    const slug = collection.slug || collection.urn;
    setView(null);
    setChips((prev) => {
      const idx = prev.findIndex(
        (c) => c.kind === 'predicate' && c.friendlyToken === 'in',
      );
      if (idx >= 0) {
        const merged = {
          ...prev[idx],
          values: Array.from(new Set([...(prev[idx].values || []), slug])),
        };
        return prev.map((c, i) => (i === idx ? merged : c));
      }
      return [...prev, {
        kind: 'predicate',
        friendlyToken: 'in',
        wirePredicate: FRIENDLY_TOKENS.in.wirePredicate,
        values: [slug],
      }];
    });
  }, [setChips, setView]);

  // JOBE-0044.9: after a successful save, anchor the URL to the new
  // view's slug. The modal's onSubmit returns the freshly-minted row.
  const onAfterSaveCurrentView = useCallback((row) => {
    if (!row || !row.slug) return;
    setView(row.slug);
  }, [setView]);

  return (
    <LibraryRail
      activeView={view}
      activeType={activeType}
      typeRail={typeRail}
      onChangeTypeRail={setTypeRail}
      onPickView={onPickView}
      onPickType={onPickType}
      onPickCollection={onPickCollection}
      currentChips={chips}
      onAfterSaveCurrentView={onAfterSaveCurrentView}
      createOpen={createOpen}
      onCreateModalClose={onCreateModalClose}
    />
  );
}

// LibraryOmniPaletteBridge: the library's omni palette wiring +
// shell registration. Reads/dispatches against the host's chip state
// via context; lives inside the host's children so it shares the
// EntityBrowserContext but renders outside the LibraryGrid (the
// palette portal-mounts its own overlay).
function LibraryOmniPaletteBridge({ onSaveView }) {
  const { setChips, mergeChipInto } = useEntityBrowserContext();
  const { types: entityTypesList } = useContext(EntityTypesContext);
  const { items: collectionsList } = useContext(CollectionsContext);
  const { kinds: documentKindsList, status: documentKindsStatus } = useContext(DocumentKindsContext);
  const agentsList = useStore((s) => s.agents.list);

  const roster = useMemo(
    () => ({ humans: [], agents: agentsList || [] }),
    [agentsList],
  );
  // Surface kinds in the palette sources only once they've loaded —
  // mirrors the TypeValuePopover gate (`status === 'ready'`).
  const paletteSources = useMemo(
    () => ({
      entityTypes: entityTypesList || [],
      collections: collectionsList || [],
      kinds: documentKindsStatus === 'ready' ? (documentKindsList || []) : [],
      roster,
    }),
    [entityTypesList, collectionsList, documentKindsList, documentKindsStatus, roster],
  );

  // JOBE-0098.5: register the omni palette with the shell so the
  // ⌘, / ⌘F / `/` chords can open it.
  const palette = useOutletContext();
  const registerOmniPalette = palette?.registerOmniPalette;
  const unregisterOmniPalette = palette?.unregisterOmniPalette;
  const omniRef = useRef(null);
  useEffect(() => {
    if (!registerOmniPalette) return undefined;
    registerOmniPalette(omniRef.current);
    return () => { unregisterOmniPalette?.(); };
  }, [registerOmniPalette, unregisterOmniPalette]);

  // Palette → chip-row bridge. Each chip threads through the host's
  // canonical merge transform so picker-, parser-, and palette-emitted
  // chips share one path. Looping inside one setChips updater keeps the
  // URL writer to a single commit.
  const onPaletteApplyFilters = useCallback((paletteChips) => {
    if (!Array.isArray(paletteChips) || paletteChips.length === 0) return;
    setChips((prev) => {
      let next = prev;
      for (const chip of paletteChips) {
        if (!chip || chip.kind !== 'predicate') continue;
        if (!chip.values || chip.values.length === 0) continue;
        next = mergeChipInto(next, chip);
      }
      return next;
    });
  }, [setChips, mergeChipInto]);

  // Palette → live-search bridge. Single page, capped at 8 rows. See
  // PR #700 reviews for the bffWired pre-filter, AbortSignal forwarding,
  // and fuzzy-fragment `q:` bridge.
  const onPaletteSearch = useCallback(async (parsed, opts = {}) => {
    const allPredicates = parsed?.predicates || [];
    const wiredPredicates = allPredicates.filter((p) => {
      if (!p || typeof p.friendlyToken !== 'string') return false;
      if (p.friendlyToken.startsWith('field.')) return false;
      const entry = FRIENDLY_TOKENS[p.friendlyToken];
      return Boolean(entry && entry.bffWired === true);
    });
    if (wiredPredicates.length === 0 && !parsed?.fuzzyFragment) {
      return { rows: [] };
    }
    const chipsForFetch = wiredPredicates.map((p) => ({
      kind: 'predicate',
      friendlyToken: p.friendlyToken,
      wirePredicate: p.rawPredicate,
      values: p.values,
    }));
    if (parsed?.fuzzyFragment) {
      chipsForFetch.push({
        kind: 'predicate',
        friendlyToken: 'q',
        wirePredicate: 'q',
        values: [parsed.fuzzyFragment],
      });
    }
    const params = chipsToFetchQuery(chipsForFetch, {
      sort: 'lastChangedAt:desc',
    });
    const r = await fetch(`/api/entities?${params.toString()}`, {
      credentials: 'same-origin',
      signal: opts.signal,
    });
    if (!r.ok) {
      throw new Error(`http_${r.status}`);
    }
    const j = await r.json();
    const items = Array.isArray(j?.items) ? j.items : [];
    return { rows: items.slice(0, 8) };
  }, []);

  // JOBE-0044.9: ⌘↩ save-view bridge. The palette's onSaveView fires
  // with (name=null, chips); we first merge the chips onto the active
  // chip row so the modal echoes the saved set, then ask the page-
  // level state to open the create modal. The hook in
  // SavedViewsBlock reads the chip row to build the query payload.
  const onPaletteSaveView = useCallback((_unusedName, paletteChips) => {
    if (Array.isArray(paletteChips) && paletteChips.length > 0) {
      setChips((prev) => {
        let next = prev;
        for (const chip of paletteChips) {
          if (!chip || chip.kind !== 'predicate') continue;
          if (!chip.values || chip.values.length === 0) continue;
          next = mergeChipInto(next, chip);
        }
        return next;
      });
    }
    if (onSaveView) onSaveView();
  }, [setChips, mergeChipInto, onSaveView]);

  return (
    <OmniPalette
      ref={omniRef}
      sources={paletteSources}
      onApplyFilters={onPaletteApplyFilters}
      onSearch={onPaletteSearch}
      onSaveView={onPaletteSaveView}
    />
  );
}
